import { Satellite, Waves, Gauge, Wind, Activity, Radio } from "lucide-react";
import { Switch } from "@/components/ui/switch";

function Metric({ icon: Icon, label, value, unit, sub }) {
  return (
    <div className="bg-slate-900/60 border border-border rounded-md px-3 py-2">
      <div className="flex items-center gap-1.5 font-mono-t text-[9px] uppercase tracking-widest text-slate-500">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="font-heading font-bold text-lg text-slate-100 leading-tight">
        {value}
        <span className="text-xs text-slate-400 ml-1">{unit}</span>
      </div>
      {sub && <div className="font-mono-t text-[9px] text-slate-500">{sub}</div>}
    </div>
  );
}

export default function TelemetryPanel({ telemetry, autonomous, setAutonomous, lastSync }) {
  const sea = telemetry?.sea_level;
  const marine = telemetry?.marine;
  const buoy = telemetry?.buoy;
  const atmos = telemetry?.atmos;
  const model = telemetry?.model;
  const waveH = marine?.wave_height_m ?? buoy?.wave_height_m;
  const pressure = model?.pressure_hpa ?? atmos?.pressure_hpa ?? buoy?.pressure_hpa;
  const wind = atmos?.wind_speed_kmh ?? buoy?.wind_speed_kmh;

  return (
    <div data-testid="telemetry-panel" className="glass rounded-lg p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Satellite className="w-4 h-4 text-cyan" />
          <h2 className="font-heading font-semibold uppercase tracking-wide text-sm text-slate-100">
            Live Telemetry
          </h2>
        </div>
        <div className="flex items-center gap-2" data-testid="autonomous-toggle">
          <span className={`font-mono-t text-[9px] uppercase tracking-widest ${autonomous ? "text-cyan" : "text-slate-500"}`}>
            {autonomous ? "AUTO" : "MANUAL"}
          </span>
          <Switch checked={autonomous} onCheckedChange={setAutonomous} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Metric icon={Waves} label="Sea Level" value={sea?.latest_m ?? "—"} unit="m"
                sub={sea ? `${sea.anomaly_m >= 0 ? "+" : ""}${sea.anomaly_m} m vs MSL` : "IOC brid2"} />
        <Metric icon={Activity} label="Wave Ht" value={waveH ?? "—"} unit="m"
                sub={marine?.wave_direction_deg != null ? `dir ${marine.wave_direction_deg}°` : (buoy ? `dir ${buoy.mean_wave_dir_deg}°` : "")} />
        <Metric icon={Gauge} label="Pressure" value={pressure ?? "—"} unit="hPa"
                sub={model?.inverse_barometer_m != null ? `IB +${model.inverse_barometer_m}m` : ""} />
        <Metric icon={Wind} label="Wind" value={wind ?? "—"} unit="km/h" />
      </div>

      {model && (
        <div className="bg-cyan/5 border border-cyan/30 rounded-md px-3 py-2">
          <div className="font-mono-t text-[9px] uppercase tracking-widest text-cyan/80 mb-1">
            Measured water level (gauge − tide model)
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div data-testid="telemetry-swl">
              <div className="font-heading font-bold text-cyan">{model.still_water_level_m ?? "—"}m</div>
              <div className="font-mono-t text-[8px] text-slate-500 uppercase">Still water</div>
            </div>
            <div data-testid="telemetry-astro-tide">
              <div className="font-heading font-bold text-cyan">{model.astronomical_tide_m ?? "—"}m</div>
              <div className="font-mono-t text-[8px] text-slate-500 uppercase">Tide (pred.)</div>
            </div>
            <div data-testid="telemetry-residual">
              <div className="font-heading font-bold text-cyan">{model.non_tidal_residual_m ?? "—"}m</div>
              <div className="font-mono-t text-[8px] text-slate-500 uppercase">Surge resid.</div>
            </div>
          </div>
          <div className="font-mono-t text-[9px] text-slate-500 mt-1">
            High-tide line (MHHW) {model.mhhw_m ?? "—"} m · max shoreline run-up {model.shoreline_runup_max_m ?? "—"} m (Stockdon 2006)
          </div>
        </div>
      )}

      <div className="flex items-center gap-1.5 font-mono-t text-[9px] uppercase tracking-wider text-slate-500">
        <Radio className="w-3 h-3 text-cyan animate-pulse" />
        {sea?.station || "IOC"} · {buoy?.station || "NDBC"} {lastSync ? `· ${lastSync}` : ""}
      </div>
    </div>
  );
}
